// A guide is a directory of numbered markdown sections (01-pallet-town.md)
// listed by its manifest.json, each with an optional front matter of a
// title and an in-game location.

export interface GuideSection {
  id: string;
  filename: string;
  title: string;
  location: string;
  markdown: string;
  order: number;
}

interface FrontMatter {
  title?: string;
  location?: string;
}

export function parseFrontMatter(content: string): { frontMatter: FrontMatter; markdown: string } {
  if (!content.startsWith("---")) return { frontMatter: {}, markdown: content };
  const lines = content.split("\n");
  const end = lines.indexOf("---", 1);
  if (end === -1) return { frontMatter: {}, markdown: content };
  const frontMatter: FrontMatter = {};
  for (const line of lines.slice(1, end)) {
    const match = /^(title|location):\s*(.+)$/.exec(line);
    if (match) frontMatter[match[1] as keyof FrontMatter] = match[2].replace(/^["']|["']$/g, "");
  }
  return { frontMatter, markdown: lines.slice(end + 1).join("\n").trim() };
}

/** A section from its file name and contents; the leading number orders it and the rest names it when the front matter doesn't. */
export function parseSection(filename: string, content: string): GuideSection {
  const { frontMatter, markdown } = parseFrontMatter(content);
  const order = parseInt(filename.split("-")[0], 10);
  const id = filename.replace(/\.md$/, "");
  return {
    id,
    filename,
    title: frontMatter.title || id.replace(/^\d+-/, "").replace(/-/g, " "),
    location: frontMatter.location || "",
    markdown,
    order: Number.isNaN(order) ? Infinity : order,
  };
}

async function fetchSection(guidePath: string, filename: string): Promise<GuideSection | null> {
  try {
    const response = await fetch(`/guides/${guidePath}/${filename}`);
    return response.ok ? parseSection(filename, await response.text()) : null;
  } catch {
    return null;
  }
}

export async function loadGuideSections(guidePath: string): Promise<GuideSection[]> {
  try {
    const response = await fetch(`/guides/${guidePath}/manifest.json`);
    if (!response.ok) return [];
    const manifest = (await response.json()) as string[] | { sections?: string[] };
    const filenames = Array.isArray(manifest) ? manifest : (manifest.sections ?? []);
    const sections = await Promise.all(filenames.map((filename) => fetchSection(guidePath, filename)));
    return sections.filter((s): s is GuideSection => s !== null).sort((a, b) => a.order - b.order);
  } catch {
    return [];
  }
}
